/**
 * Sunucu kodu -> CEVIRI ANAHTARI haritalari (platform yonetimi).
 *
 * DEGERLER METIN DEGIL ANAHTAR. Bu dosya modul seviyesinde yasiyor; burada t()
 * cagirmak metni IMPORT ANINDA dondurur ve dil degistiginde bayat birakir.
 * Adlarin `..._LABEL_KEYS` olmasi, kullanim hatasini OKURKEN belli etsin diye:
 * `{AUDIT_ACTION_LABEL_KEYS[c]}` ekranda ham anahtar basar, dogrusu
 * `{t(AUDIT_ACTION_LABEL_KEYS[c])}`. Bu kural src/i18n/__tests__/keys.test.ts
 * tarafindan zorlaniyor.
 *
 * Anahtarlarin gercekten var oldugunu __tests__/labels.test.ts dogruluyor.
 */

/** Kullanici tarafi rozeti. */
export const USER_TYPE_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  customer: 'admin.userTypes.customer',
  exchange: 'admin.userTypes.exchange',
});

/** Platform rolu. `super_admin` bu bolgeye giren TEK rol. */
export const ROLE_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  super_admin: 'admin.roles.superAdmin',
  user: 'admin.roles.user',
});

/** Buro ICINDEKI otorite — platform rolunden bagimsiz eksen. */
export const MEMBER_TYPE_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  admin: 'members.types.admin',
  staff: 'members.types.staff',
});

/**
 * Denetim kaydi eylem kodu -> etiket.
 *
 * IKI KAYNAK BIR ARADA: platform kaydinda hem `organizationId: null` olan
 * PLATFORM eylemleri (adminUserDeactivated…) hem de burolarin kendi
 * eylemleri (memberProvisioned…) gorunur. Buro eylemleri icin var olan
 * `auditActions.*` anahtarlari YENIDEN KULLANILIYOR — ayni kavramin iki metni
 * olsaydi biri guncellenip oteki bayat kalirdi.
 */
export const ADMIN_AUDIT_ACTION_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  // Platform seviyesi (organizationId: null)
  adminUserActivated: 'admin.auditActions.userActivated',
  adminUserDeactivated: 'admin.auditActions.userDeactivated',
  adminRoleChanged: 'admin.auditActions.roleChanged',
  adminPasswordReset: 'admin.auditActions.passwordReset',
  adminOrgActivated: 'admin.auditActions.orgActivated',
  adminOrgDeactivated: 'admin.auditActions.orgDeactivated',
  // 2026-08-30: platform artik organizasyon ve kullanici YARATIYOR (signup kapatildi).
  adminOrgCreated: 'admin.auditActions.orgCreated',
  adminMemberProvisioned: 'admin.auditActions.memberProvisioned',
  // IBAN'lar platform yonetiminde: hesap tanimlama/guncelleme de denetim kaydina duser.
  adminAccountCreated: 'admin.auditActions.accountCreated',
  adminAccountUpdated: 'admin.auditActions.accountUpdated',
  // Isletme seviyesi — mevcut katalog
  memberInvited: 'auditActions.memberInvited',
  invitationCancelled: 'auditActions.invitationCancelled',
  invitationAccepted: 'auditActions.invitationAccepted',
  memberProvisioned: 'auditActions.memberProvisioned',
  memberRemoved: 'auditActions.memberRemoved',
  memberDeactivated: 'auditActions.memberDeactivated',
  memberUpdated: 'auditActions.memberUpdated',
  permissionGranted: 'auditActions.permissionGranted',
  permissionRevoked: 'auditActions.permissionRevoked',
  organizationUpdated: 'auditActions.organizationUpdated',
  accountCreated: 'auditActions.accountCreated',
  accountUpdated: 'auditActions.accountUpdated',
  accountDeactivated: 'auditActions.accountDeactivated',
});

/** Filtre listesinin sirasi — Object.keys sirasina guvenilmiyor. */
export const ADMIN_AUDIT_ACTION_CODES: readonly string[] = Object.freeze(
  Object.keys(ADMIN_AUDIT_ACTION_LABEL_KEYS),
);

/** Duyuru hedef kitlesi. */
export const AUDIENCE_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  customer: 'admin.broadcast.audienceCustomer',
  exchange: 'admin.broadcast.audienceExchange',
  all: 'admin.broadcast.audienceAll',
});

/** Metrik penceresi. */
export const PERIOD_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  day: 'admin.metrics.periodDay',
  week: 'admin.metrics.periodWeek',
  month: 'admin.metrics.periodMonth',
});

/** Zaman serisi olcusu. */
export const TIMESERIES_METRIC_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  tasks: 'admin.metrics.metricTasks',
  offers: 'admin.metrics.metricOffers',
  volume: 'admin.metrics.metricVolume',
});

/** Akis olayi turu. */
export const ACTIVITY_EVENT_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  task: 'admin.activity.eventTask',
  offer: 'admin.activity.eventOffer',
});

/**
 * Sunucu hata KODU -> kullaniciya gosterilecek metnin anahtari.
 *
 * Bu uclar Turkce mesaj DEGIL sabit bir KOD doner. Ham kodu basmak kullaniciya
 * hicbir sey anlatmaz; ozellikle iki koruma kuralinin ("kendini degistiremezsin",
 * "son super_admin dusurulemez") NEDENIYLE birlikte anlatilmasi gerekiyor.
 */
export const ADMIN_ERROR_MESSAGE_KEYS: Readonly<Record<string, string>> = Object.freeze({
  CANNOT_MODIFY_SELF: 'admin.errors.cannotModifySelf',
  LAST_SUPER_ADMIN_PROTECTED: 'admin.errors.lastSuperAdminProtected',
  USER_NOT_FOUND: 'admin.errors.userNotFound',
  USER_ID_REQUIRED: 'admin.errors.userNotFound',
  USER_STATUS_UPDATE_FAILED: 'admin.errors.statusUpdateFailed',
  USER_ROLE_UPDATE_FAILED: 'admin.errors.roleUpdateFailed',
  PASSWORD_RESET_FAILED: 'admin.errors.passwordResetFailed',
  INVALID_USER_ROLE_CODE: 'admin.errors.invalidRole',
  // Platform yoneticiligi API'den verilemez; yalniz veritabanindan verilir.
  SUPER_ADMIN_NOT_ASSIGNABLE: 'admin.errors.superAdminNotAssignable',
  INVALID_IS_ACTIVE: 'admin.errors.invalidStatus',
  IS_ACTIVE_REQUIRED: 'admin.errors.invalidStatus',
  ORGANIZATION_NOT_FOUND: 'admin.errors.organizationNotFound',
  ORGANIZATION_ID_REQUIRED: 'admin.errors.organizationNotFound',
  ORGANIZATION_STATUS_UPDATE_FAILED: 'admin.errors.statusUpdateFailed',
  INVALID_ORGANIZATION_ID: 'admin.errors.organizationNotFound',
  INVALID_ACTOR_USER_ID: 'admin.errors.invalidFilter',
  INVALID_SORT_FIELD: 'admin.errors.invalidFilter',
  INVALID_PERIOD: 'admin.errors.invalidFilter',
  INVALID_FROM: 'admin.errors.invalidDateRange',
  INVALID_TO: 'admin.errors.invalidDateRange',
  INVALID_DATE_RANGE: 'admin.errors.invalidDateRange',
  UNSUCCESSFULLY_ADMIN_METRICS_GET: 'admin.errors.metricsFailed',
  // Organizasyon ve uye yaratma (2026-08-30)
  ORGANIZATION_NAME_REQUIRED: 'admin.errors.organizationNameRequired',
  INVALID_ORGANIZATION_TYPE_CODE: 'admin.errors.invalidOrganizationType',
  INVALID_EMAIL: 'admin.errors.invalidEmail',
  FULL_NAME_REQUIRED: 'admin.errors.fullNameRequired',
  // Oda/sicil yalniz doviz burosunda zorunlu; musteri org'unda istenmez.
  BUSINESS_REGISTRATION_REQUIRED: 'admin.errors.businessRegistrationRequired',
  EMAIL_ALREADY_REGISTERED: 'admin.errors.emailAlreadyRegistered',
  INVALID_MEMBER_TYPE: 'admin.errors.invalidMemberType',
  // Yonetici ORTUK tam yetkilidir; ona ayrica yetki atamak yaniltici bir kayit birakirdi.
  ADMIN_PERMISSIONS_IMPLICIT: 'admin.errors.adminPermissionsImplicit',
  PERMISSION_NOT_GRANTABLE: 'admin.errors.permissionNotGrantable',
  // Banka hesaplari (IBAN platform yonetiminde)
  ACCOUNT_NOT_FOUND: 'admin.errors.accountNotFound',
  // Sunucu IBAN dogrulamasi (2026-09-07). Kod eslenmezse admin ekraninda ham
  // 'ACCOUNT_IBAN_INVALID' gorunurdu.
  ACCOUNT_IBAN_INVALID: 'admin.errors.accountIbanInvalid',
  ACCOUNT_ID_INVALID: 'admin.errors.accountNotFound',
  ACCOUNT_LIST_FAILED: 'admin.errors.accountListFailed',
  // WHERE'i hicbir satiri tutmayan UPDATE: basari demek IBAN degisti demek olurdu.
  ACCOUNT_UPDATE_NO_OP: 'admin.errors.accountUpdateNoOp',
  // Duyuru
  INVALID_AUDIENCE: 'admin.errors.invalidAudience',
  TITLE_REQUIRED: 'admin.errors.titleRequired',
  BODY_REQUIRED: 'admin.errors.bodyRequired',
  TITLE_TOO_LONG: 'admin.errors.titleTooLong',
  BODY_TOO_LONG: 'admin.errors.bodyTooLong',
  INVALID_TARGET_USER_ID: 'admin.errors.invalidTargetUser',
  BROADCAST_DELIVERY_FAILED: 'admin.errors.broadcastFailed',
  UNSUCCESSFULLY_BROADCAST_LOG_CREATE: 'admin.errors.broadcastFailed',
  FORBIDDEN: 'admin.errors.forbidden',
  // Uyari cozumu (currency admin-service, 2026-09-23). Baska bir yonetici once kapattiysa 409.
  TASK_NOT_IN_WARNING: 'admin.errors.taskNotInWarning',
  TASK_ID_REQUIRED: 'admin.errors.taskNotInWarning',
  INVALID_RESOLUTION: 'admin.errors.invalidResolution',
  RESOLUTION_NOTE_TOO_LONG: 'admin.errors.resolutionNoteTooLong',
});

/** Uyari cozumu sonucu -> etiket (secim dugmeleri ve basari mesaji). */
export const RESOLUTION_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  resolved: 'admin.warned.resolved',
  refunded: 'admin.warned.refunded',
});

/** Secimin ne anlama geldigi — onay adiminda okunur. */
export const RESOLUTION_HINT_KEYS: Readonly<Record<string, string>> = Object.freeze({
  resolved: 'admin.warned.resolvedHint',
  refunded: 'admin.warned.refundedHint',
});
